import React, { useState, useEffect } from 'react';
import {

  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  CircularProgress,
  useTheme,
  Divider,
  IconButton,
  Drawer,
  useMediaQuery,
  backdropClasses,
} from '@mui/material';
import CustomButton from '../widgets/CustomButton';
import {
  Close,
  Movie,
  Bookmark,
  Favorite,
  RateReview,
  People,
  PersonAdd,
  PlaylistAdd,
  ThumbUp,
  BorderColor,
} from '@mui/icons-material';
import { UserProfile, accessControlService } from '@watchlist/shared';
import { setUserBlocked } from '../../services/functionsApi';
import InitialsAvatar from '../widgets/InitialsAvatar';
import UserRoleChip from './UserRoleChip';
import { glassStyles } from 'tokens/designTokens';
import { font } from '../../utils/fontHelper';
import { parseFirebaseDate, formatFirebaseDate } from '../../utils/dateUtils';
import { useToast } from '../utils/ToastProvider';

interface UserDetailDialogProps {
  open: boolean;
  onClose: () => void;
  user: UserProfile | null;
  onUserUpdated?: (uid: string, changes: Partial<UserProfile>) => void;
}

interface UserDetailedStats {
  watchlistsCreated: number;
  watchlistsLiked: number;
  totalWatchlistLikes: number;
  moviesWatched: number;
  moviesWatchlisted: number;
  reviewsWritten: number;
  followersCount: number;
  followingCount: number;
}

const UserDetailDialog: React.FC<UserDetailDialogProps> = ({ open, onClose, user, onUserUpdated }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { showInfo, showWarning } = useToast();

  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<UserDetailedStats | null>(null);
  const [blockLoading, setBlockLoading] = useState(false);

  useEffect(() => {
    if (open && user) {
      loadUserStats();
    }
  }, [open, user]);

  const loadUserStats = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const detailedStats = await accessControlService.getUserDetailedStats(user.uid);
      setStats(detailedStats);
    } catch (error) {
      console.error('Error loading user stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBlockToggle = async (block: boolean) => {
    if (!user) return;
    setBlockLoading(true);
    try {
      await setUserBlocked(user.uid, block);
      onUserUpdated?.(user.uid, { isBlocked: block } as any);
      showInfo(block ? `${user.displayName || 'User'} has been blocked` : `${user.displayName || 'User'} has been unblocked`);
      onClose();
    } catch (e) {
      console.error('Block toggle failed', e);
      showWarning(block ? 'Failed to block user. Please try again.' : 'Failed to unblock user. Please try again.');
    } finally {
      setBlockLoading(false);
    }
  };

  const formatDate = (timestamp: any) => {
    return formatFirebaseDate(timestamp, 'Unknown', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!user) return null;

  const dialogContent = (
    <>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          pt: isMobile ? 1 : 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: font() }}>
          User Details
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{
        pb: isMobile ? Math.max(3, parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sait') || '0') + 3) : 3,
      }}>
        {/* User Profile Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 3,
            p: 2,
            bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
            borderRadius: 2,
            mt: 1,
          }}
        >
          {user.photoURL ? (
            <Box
              component="img"
              src={user.photoURL}
              alt=""
              sx={{ width: 64, height: 64, borderRadius: '50%', flexShrink: 0 }}
            />
          ) : (
            <InitialsAvatar name={user.displayName} size={64} />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 18,
                  color: theme.palette.text.primary,
                }}
              >
                {user.displayName}
              </Typography>
              <UserRoleChip role={user.role || 'regular'} size="small" />
            </Box>
            {user.username && (
              <Typography sx={{ fontSize: 14, color: theme.palette.text.secondary }}>
                @{user.username}
              </Typography>
            )}
            <Typography sx={{ fontSize: 13, color: theme.palette.text.disabled }}>
              {user.email}
            </Typography>
          </Box>
        </Box>

        {/* Account Info */}
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 14,
              color: theme.palette.text.secondary,
              mb: 1,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            Account Info
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 12, color: theme.palette.text.disabled }}>
                Joined
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                {formatDate(user.createdAt)}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12, color: theme.palette.text.disabled }}>
                Last Updated
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                {formatDate(user.updatedAt)}
              </Typography>
            </Box>
            {user.bio && (
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Typography sx={{ fontSize: 12, color: theme.palette.text.disabled }}>
                  Bio
                </Typography>
                <Typography sx={{ fontSize: 14 }}>{user.bio}</Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* User Statistics */}
        <Box>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 14,
              color: theme.palette.text.secondary,
              mb: 2,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            User Statistics
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : stats ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 2,
              }}
            >
              <StatItem
                icon={<Movie sx={{ fontSize: 20 }} />}
                label="Movies Watched"
                value={stats.moviesWatched}
                color="#30D158"
                isDark={isDark}
              />
              <StatItem
                icon={<Bookmark sx={{ fontSize: 20 }} />}
                label="Movies Watchlisted"
                value={stats.moviesWatchlisted}
                color="#FF9F0A"
                isDark={isDark}
              />
              <StatItem
                icon={<PlaylistAdd sx={{ fontSize: 20 }} />}
                label="Watchlists Created"
                value={stats.watchlistsCreated}
                color="#007AFF"
                isDark={isDark}
              />
              <StatItem
                icon={<Favorite sx={{ fontSize: 20 }} />}
                label="Watchlists Liked"
                value={stats.watchlistsLiked}
                color="#FF453A"
                isDark={isDark}
              />
              <StatItem
                icon={<ThumbUp sx={{ fontSize: 20 }} />}
                label="Total Likes Received"
                value={stats.totalWatchlistLikes}
                color="#BF5AF2"
                isDark={isDark}
              />
              <StatItem
                icon={<RateReview sx={{ fontSize: 20 }} />}
                label="Reviews Written"
                value={stats.reviewsWritten}
                color="#64D2FF"
                isDark={isDark}
              />
              <StatItem
                icon={<People sx={{ fontSize: 20 }} />}
                label="Followers"
                value={stats.followersCount}
                color="#FF375F"
                isDark={isDark}
              />
              <StatItem
                icon={<PersonAdd sx={{ fontSize: 20 }} />}
                label="Following"
                value={stats.followingCount}
                color="#5E5CE6"
                isDark={isDark}
              />
            </Box>
          ) : (
            <Typography sx={{ textAlign: 'center', color: theme.palette.text.secondary, py: 2 }}>
              Unable to load statistics
            </Typography>
          )}
        </Box>

        {/* Moderation Status */}
        <Divider sx={{ my: 2 }} />
        {(user as any).isBlocked ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 2, py: 1.5, borderRadius: 2, bgcolor: 'error.main' }}>
            <Typography sx={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>
              ⛔ Blocked by a moderator
            </Typography>
            <Button
              size="small"
              variant="contained"
              disabled={blockLoading}
              onClick={() => handleBlockToggle(false)}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', textTransform: 'none', fontWeight: 600, fontSize: 12, '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }, '&:disabled': { opacity: 0.5 } }}
            >
              {blockLoading ? 'Unblocking…' : 'Unblock'}
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              variant="outlined"
              disabled={blockLoading}
              onClick={() => handleBlockToggle(true)}
              sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12, color: 'error.main', borderColor: 'error.main', '&:hover': { bgcolor: 'error.main', color: '#fff' }, '&:disabled': { opacity: 0.5 } }}
            >
              {blockLoading ? 'Blocking…' : 'Block user'}
            </Button>
          </Box>
        )}

        {/* Privacy Settings */}
        {user.privacy && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: 14,
                  color: theme.palette.text.secondary,
                  mb: 1,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                Privacy Settings
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <PrivacyChip
                  label={`Profile: ${user.privacy.profileVisibility}`}
                  isDark={isDark}
                />
                <PrivacyChip
                  label={`Watchlist: ${user.privacy.showWatchlist ? 'Visible' : 'Hidden'}`}
                  isDark={isDark}
                />
                <PrivacyChip
                  label={`Ratings: ${user.privacy.showRatings ? 'Visible' : 'Hidden'}`}
                  isDark={isDark}
                />
                <PrivacyChip
                  label={`Activity: ${user.privacy.showActivity ? 'Visible' : 'Hidden'}`}
                  isDark={isDark}
                />
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
    </>
  );

  return isMobile ? (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: false }}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          bgcolor: isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.3)',
          ...glassStyles,
          backdropFilter: 'blur(10px)',
          border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'}`,
          maxHeight: '90vh',
        }
      }}
    >

      {dialogContent}
    </Drawer>
  ) : (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: isDark ? '#1C1C1E' : '#FFFFFF',
          
        },
      }}
    >
      {dialogContent}
    </Dialog>
  );
};

// Stat Item Component
interface StatItemProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: string;
  isDark: boolean;
}

const StatItem: React.FC<StatItemProps> = ({ icon, label, value, color, isDark }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      p: 1.5,
      bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
      borderRadius: 2,
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 36,
        height: 36,
        borderRadius: '50%',
        bgcolor: `${color}20`,
        color: color,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography
        sx={{
          fontSize: 20,
          fontWeight: 700,
          color: color,
          lineHeight: 1,
        }}
      >
        {value.toLocaleString()}
      </Typography>
      <Typography
        sx={{
          fontSize: 11,
          color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>
    </Box>
  </Box>
);

// Privacy Chip Component
interface PrivacyChipProps {
  label: string;
  isDark: boolean;
}

const PrivacyChip: React.FC<PrivacyChipProps> = ({ label, isDark }) => (
  <Box
    sx={{
      px: 1.5,
      py: 0.5,
      borderRadius: 1,
      bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
      fontSize: 12,
      fontWeight: 500,
    }}
  >
    {label}
  </Box>
);

export default UserDetailDialog;
